/**
 * Analytics Provider for React
 */

'use client';

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { analytics } from './index';
import { authEvents } from './events/auth';
import { financialEvents } from './events/financial';
import { hasConsent } from './consent';
import type { UserTraits } from './types';

interface AnalyticsContextValue {
  analytics: typeof analytics;
  authEvents: typeof authEvents;
  financialEvents: typeof financialEvents;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
  userId?: string;
  userTraits?: UserTraits;
}

export function AnalyticsProvider({ 
  children, 
  userId,
  userTraits 
}: AnalyticsProviderProps) {
  // Identify user when provider mounts or userId changes
  useEffect(() => {
    if (userId && hasConsent()) {
      analytics.identify(userId, userTraits);
    }
  }, [userId, userTraits]);

  // Track page views on route change
  useEffect(() => {
    // Initial page view
    analytics.page();

    // Listen for route changes (Next.js specific)
    const handleRouteChange = () => {
      analytics.page();
    };

    // For Next.js app router, we need to use a different approach
    if (typeof window !== 'undefined') {
      // Use MutationObserver to detect route changes
      const observer = new MutationObserver(() => {
        const currentPath = window.location.pathname;
        const lastPath = sessionStorage.getItem('lastPath');
        
        if (currentPath !== lastPath) {
          sessionStorage.setItem('lastPath', currentPath);
          handleRouteChange();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // Track Web Vitals
  useEffect(() => {
    if (typeof window !== 'undefined' && 'web-vital' in window) {
      // Track Core Web Vitals
      const reportWebVital = ({ name, value }: { name: string; value: number }) => {
        analytics.performance(name, Math.round(value), 'ms');
      };

      // Next.js Web Vitals API
      if ('reportWebVitals' in window) {
        (window as any).reportWebVitals(reportWebVital);
      }
    }
  }, []);

  const value: AnalyticsContextValue = {
    analytics,
    authEvents,
    financialEvents,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Hook to use analytics context
 */
export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider');
  }
  
  return context;
}